import { Box, Typography } from '@mui/material'
import React, { useRef, useState } from 'react'
import { formatDateTime } from '../../helpers/dateHelper'

// Supported annotation shape types
export const ANNOTATION_MODES = [
  'MOVE',
  'RECTANGLE',
  'OVAL',
  'DRAWING',
  'HIGHLIGHTER',
  'POINT',
]

const SHAPE_COLOR = '#cc0000'
const HIGHLIGHTER_COLOR = '#FFD700'

export default function ImageAnnotations({
  containerRef,
  enableAnnotations,
  annotations,
  onAnnotationsChange,
  userInfo = {},
  onZoom,
  mode = 'RECTANGLE',
}) {
  const { lanId = '', displayname = '', email = '' } = userInfo || {}
  const userDisplayName = displayname || lanId || email || 'User'
  const [draft, setDraft] = useState(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [pending, setPending] = useState(null) // shape waiting for comment input
  const [commentText, setCommentText] = useState('')
  const [dragState, setDragState] = useState(null) // for MOVE mode
  const [resizeState, setResizeState] = useState(null) // for RESIZE: {id, handle, startGeometry, startMouseX, startMouseY, rect}
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState(null) // hover popup
  const [activeAnnotationId, setActiveAnnotationId] = useState(null)
  const [threadCommentText, setThreadCommentText] = useState('')
  const suppressClickUntilRef = useRef(0)

  const getLocalDateTimeString = (value = new Date()) => {
    if (typeof value === 'string') {
      return value.length >= 19 ? value.slice(0, 19) : value
    }

    const dateValue = new Date(value)
    if (Number.isNaN(dateValue.getTime())) {
      return new Date().toISOString().slice(0, 19)
    }

    const pad = (input = 0) => `${input}`.padStart(2, '0')
    return `${dateValue.getFullYear()}-${pad(dateValue.getMonth() + 1)}-${pad(
      dateValue.getDate()
    )}T${pad(dateValue.getHours())}:${pad(dateValue.getMinutes())}:${pad(
      dateValue.getSeconds()
    )}`
  }

  const normalizeComment = (comment = {}) => ({
    ...comment,
    created_dt: getLocalDateTimeString(comment.created_dt || comment.createdAt),
  })

  const getGeometry = (item = {}) => {
    if (item.geometry && typeof item.geometry === 'object') {
      return item.geometry
    }

    return {
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
      points: item.points,
      anchorX: item.anchorX,
      anchorY: item.anchorY,
    }
  }

  const toPercent = (value, total) =>
    total > 0 ? Math.max(0, Math.min(100, (value / total) * 100)) : 0

  const getRelativePos = (event) => {
    const rect = containerRef.current.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      rect,
    }
  }

  // ── Mouse handlers ──────────────────────────────────────────────────────────

  const handleMouseDown = (event) => {
    if (!enableAnnotations || !containerRef?.current) return
    if (mode === 'MOVE') return // handled per-annotation
    event.preventDefault()

    const { x, y, rect } = getRelativePos(event)

    if (mode === 'POINT') {
      setPending({
        type: 'POINT',
        geometry: {
          left: toPercent(x, rect.width),
          top: toPercent(y, rect.height),
          anchorX: toPercent(x, rect.width),
          anchorY: toPercent(y, rect.height),
        },
      })
      setCommentText('')
      return
    }

    setIsDrawing(true)
    if (mode === 'DRAWING' || mode === 'HIGHLIGHTER') {
      setDraft({
        type: mode,
        geometry: {
          points: [
            { x: toPercent(x, rect.width), y: toPercent(y, rect.height) },
          ],
        },
        rawRect: rect,
      })
    } else {
      // RECTANGLE | OVAL | HIGHLIGHTER (box)
      setDraft({ type: mode, start: { x, y }, rawRect: rect })
    }
  }

  const handleMouseMove = (event) => {
    // Handle RESIZE
    if (resizeState) {
      setResizeState((prev) =>
        prev
          ? {
              ...prev,
              currentMouseX: event.clientX,
              currentMouseY: event.clientY,
            }
          : prev
      )
      return
    }

    // Handle MOVE
    if (mode === 'MOVE' && dragState) {
      setDragState((prev) =>
        prev
          ? {
              ...prev,
              currentMouseX: event.clientX,
              currentMouseY: event.clientY,
            }
          : prev
      )
      return
    }

    if (!isDrawing || !draft) return
    const rect = draft.rawRect
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    if (draft.type === 'DRAWING' || draft.type === 'HIGHLIGHTER') {
      setDraft((prev) => ({
        ...prev,
        geometry: {
          ...prev.geometry,
          points: [
            ...(prev.geometry?.points || []),
            { x: toPercent(x, rect.width), y: toPercent(y, rect.height) },
          ],
        },
      }))
    } else {
      const x1 = Math.min(draft.start.x, x)
      const y1 = Math.min(draft.start.y, y)
      const x2 = Math.max(draft.start.x, x)
      const y2 = Math.max(draft.start.y, y)
      setDraft((prev) => ({
        ...prev,
        box: {
          left: toPercent(x1, rect.width),
          top: toPercent(y1, rect.height),
          width: toPercent(x2 - x1, rect.width),
          height: toPercent(y2 - y1, rect.height),
        },
      }))
    }
  }

  const handleMouseUp = () => {
    // Handle RESIZE end
    if (resizeState) {
      const newGeometry = getResizedGeometry(resizeState)
      if (newGeometry) {
        onAnnotationsChange(
          annotations.map((a) =>
            a.id === resizeState.id
              ? {
                  ...a,
                  geometry: {
                    ...getGeometry(a),
                    ...newGeometry,
                  },
                }
              : a
          )
        )
      }
      suppressClickUntilRef.current = Date.now() + 250
      setResizeState(null)
      return
    }

    if (dragState) {
      const movedPixels = Math.hypot(
        dragState.currentMouseX - dragState.startMouseX,
        dragState.currentMouseY - dragState.startMouseY
      )
      const dx =
        ((dragState.currentMouseX - dragState.startMouseX) /
          dragState.rect.width) *
        100
      const dy =
        ((dragState.currentMouseY - dragState.startMouseY) /
          dragState.rect.height) *
        100
      const nextLeft = Math.max(0, dragState.startLeft + dx)
      const nextTop = Math.max(0, dragState.startTop + dy)
      if (nextLeft !== dragState.startLeft || nextTop !== dragState.startTop) {
        onAnnotationsChange(
          annotations.map((a) =>
            a.id === dragState.id
              ? {
                  ...a,
                  geometry: {
                    ...getGeometry(a),
                    left: nextLeft,
                    top: nextTop,
                  },
                }
              : a
          )
        )
        suppressClickUntilRef.current = Date.now() + 250
      }
      if (movedPixels > 3) {
        suppressClickUntilRef.current = Date.now() + 250
      }
      setDragState(null)
      return
    }
    if (!isDrawing || !draft) return
    setIsDrawing(false)

    if (draft.type === 'DRAWING' || draft.type === 'HIGHLIGHTER') {
      const draftPoints = draft.geometry?.points || []
      if (draftPoints.length > 2) {
        setPending({
          type: draft.type,
          geometry: {
            points: draftPoints,
            anchorX: draftPoints[0].x,
            anchorY: draftPoints[0].y,
          },
        })
        setCommentText('')
      }
    } else if (draft.box) {
      const tooSmall = draft.box.width < 1 || draft.box.height < 1
      if (!tooSmall) {
        setPending({
          type: draft.type,
          geometry: {
            ...draft.box,
            anchorX: draft.box.left,
            anchorY: draft.box.top + draft.box.height,
          },
        })
        setCommentText('')
      }
    }
    setDraft(null)
  }

  // ── Annotation comment submit/cancel ──────────────────────────────────────

  const getThreadComments = (item = {}) => {
    if (Array.isArray(item.comments)) {
      return item.comments.map((comment = {}) => normalizeComment(comment))
    }
    if (typeof item.comment === 'string' && item.comment.trim()) {
      return [
        normalizeComment({
          id: `${item.id || Date.now()}-legacy`,
          author: userDisplayName || 'User',
          created_by: {
            login_id: userInfo.lanId || '',
            display_name: userInfo.displayname || '',
            email_address: userInfo.email || '',
          },
          note: item.comment,
          created_dt: getLocalDateTimeString(),
        }),
      ]
    }
    return []
  }

  const submitAnnotation = () => {
    if (!pending) return
    const initialText = commentText.trim()
    onAnnotationsChange([
      ...annotations,
      {
        id: Date.now(),
        comments: initialText
          ? [
              normalizeComment({
                id: `${Date.now()}-0`,
                author: userDisplayName || 'User',
                created_by: {
                  login_id: userInfo.lanId || '',
                  display_name: userInfo.displayname || '',
                  email_address: userInfo.email || '',
                },
                note: initialText,
                created_dt: getLocalDateTimeString(),
              }),
            ]
          : [],
        ...pending,
      },
    ])
    setPending(null)
    setCommentText('')
  }

  const cancelAnnotation = () => {
    setPending(null)
    setCommentText('')
  }

  const deleteAnnotation = (id) => {
    onAnnotationsChange(annotations.filter((item) => item.id !== id))
    setActiveAnnotationId(null)
    setHoveredAnnotationId(null)
    setThreadCommentText('')
  }

  const addThreadComment = () => {
    const targetId = activeAnnotationId
    if (!targetId) return
    const nextText = threadCommentText.trim()
    if (!nextText) return

    onAnnotationsChange(
      annotations.map((item) => {
        if (item.id !== targetId) return item
        const existingComments = getThreadComments(item)
        return {
          ...item,
          comments: [
            ...existingComments,
            normalizeComment({
              id: `${Date.now()}-${existingComments.length}`,
              author: userDisplayName || 'User',
              created_by: {
                login_id: userInfo.lanId || '',
                display_name: userInfo.displayname || '',
                email_address: userInfo.email || '',
              },
              note: nextText,
              created_dt: getLocalDateTimeString(),
            }),
          ],
        }
      })
    )
    setThreadCommentText('')
  }

  // ── Resize handlers ───────────────────────────────────────────────────────

  const handleResizeMouseDown = (event, annotation, handle) => {
    event.stopPropagation()
    event.preventDefault()
    const rect = containerRef.current.getBoundingClientRect()
    const annotationGeometry = getGeometry(annotation)
    setResizeState({
      id: annotation.id,
      handle,
      startGeometry: annotationGeometry,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      currentMouseX: event.clientX,
      currentMouseY: event.clientY,
      rect,
    })
  }

  const getResizedGeometry = (state) => {
    if (!state || !state.currentMouseX) return null
    const dx =
      ((state.currentMouseX - state.startMouseX) / state.rect.width) * 100
    const dy =
      ((state.currentMouseY - state.startMouseY) / state.rect.height) * 100
    const { left, top, width, height } = state.startGeometry
    const handle = state.handle

    let newLeft = left
    let newTop = top
    let newWidth = width
    let newHeight = height

    // Adjust based on which handle is being dragged
    if (handle.includes('n')) {
      newTop = top + dy
      newHeight = height - dy
    }
    if (handle.includes('s')) {
      newHeight = height + dy
    }
    if (handle.includes('w')) {
      newLeft = left + dx
      newWidth = width - dx
    }
    if (handle.includes('e')) {
      newWidth = width + dx
    }

    // Prevent negative dimensions
    if (newWidth < 1) {
      newWidth = 1
      newLeft = left
    }
    if (newHeight < 1) {
      newHeight = 1
      newTop = top
    }

    // Keep within bounds
    newLeft = Math.max(0, Math.min(100 - newWidth, newLeft))
    newTop = Math.max(0, Math.min(100 - newHeight, newTop))

    return { left: newLeft, top: newTop, width: newWidth, height: newHeight }
  }

  const renderResizeHandles = (annotation) => {
    const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

    const getHandleStyle = (handle) => {
      const base = {
        position: 'absolute',
        width: 8,
        height: 8,
        background: '#fff',
        border: '2px solid #cc0000',
        borderRadius: '50%',
        zIndex: 10,
        pointerEvents: 'auto',
      }

      const position = {}
      if (handle.includes('n')) position.top = -4
      if (handle.includes('s')) position.bottom = -4
      if (handle.includes('w')) position.left = -4
      if (handle.includes('e')) position.right = -4
      if (handle === 'n' || handle === 's') position.left = 'calc(50% - 4px)'
      if (handle === 'w' || handle === 'e') position.top = 'calc(50% - 4px)'

      const cursors = {
        nw: 'nw-resize',
        n: 'n-resize',
        ne: 'ne-resize',
        e: 'e-resize',
        se: 'se-resize',
        s: 's-resize',
        sw: 'sw-resize',
        w: 'w-resize',
      }
      return { ...base, ...position, cursor: cursors[handle] }
    }

    return handles.map((handle) => (
      <div
        key={handle}
        style={getHandleStyle(handle)}
        onMouseDown={(e) => handleResizeMouseDown(e, annotation, handle)}
      />
    ))
  }

  // ── Move mode: drag existing annotation ───────────────────────────────────

  const handleAnnotationMouseDown = (event, annotation) => {
    if (mode !== 'MOVE') return
    event.stopPropagation()
    const rect = containerRef.current.getBoundingClientRect()
    const annotationGeometry = getGeometry(annotation)
    setDragState({
      id: annotation.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      currentMouseX: event.clientX,
      currentMouseY: event.clientY,
      startLeft: annotationGeometry.left || 0,
      startTop: annotationGeometry.top || 0,
      rect,
    })
  }

  const getRenderedGeometry = (item = {}) => {
    const geometry = getGeometry(item)

    // Handle resize preview
    if (resizeState && resizeState.id === item.id) {
      const resized = getResizedGeometry(resizeState)
      return resized ? { ...geometry, ...resized } : geometry
    }

    // Handle move preview
    if (!dragState || dragState.id !== item.id || mode !== 'MOVE') {
      return geometry
    }
    const dx =
      ((dragState.currentMouseX - dragState.startMouseX) /
        dragState.rect.width) *
      100
    const dy =
      ((dragState.currentMouseY - dragState.startMouseY) /
        dragState.rect.height) *
      100
    return {
      ...geometry,
      left: Math.max(0, dragState.startLeft + dx),
      top: Math.max(0, dragState.startTop + dy),
    }
  }

  const handleAnnotationClick = (event, annotationId) => {
    if (
      resizeState ||
      dragState ||
      Date.now() < suppressClickUntilRef.current
    ) {
      return
    }
    event.stopPropagation()
    setActiveAnnotationId(annotationId)
  }

  // ── Threaded comments overlay ─────────────────────────────────────────────

  const getCommentAnchor = (item) => {
    const geometry = getGeometry(item)
    // Freehand shapes: compute bounding centre from points array
    if (
      (item.type === 'DRAWING' || item.type === 'HIGHLIGHTER') &&
      Array.isArray(geometry.points) &&
      geometry.points.length > 0
    ) {
      const xs = geometry.points.map((p) => p.x)
      const ys = geometry.points.map((p) => p.y)
      const midX = (Math.min(...xs) + Math.max(...xs)) / 2
      const minY = Math.min(...ys)
      return {
        left: Math.max(2, Math.min(78, midX)),
        top: Math.max(2, Math.min(88, minY)),
      }
    }
    if (item.type === 'POINT') {
      return {
        left: Math.max(2, Math.min(78, geometry.left || 0)),
        top: Math.max(2, Math.min(88, geometry.top || 0)),
      }
    }
    // RECTANGLE / OVAL: centre horizontally, anchor to top edge
    return {
      left: Math.max(
        2,
        Math.min(
          78,
          (geometry.left || 0) + (geometry.width ? geometry.width / 2 : 0)
        )
      ),
      top: Math.max(2, Math.min(88, geometry.top || 0)),
    }
  }

  const renderThreadOverlay = (item) => {
    if (!item || !item.id) return null
    const anchor = getCommentAnchor(item)
    const comments = getThreadComments(item)
    return (
      <div
        style={{
          position: 'absolute',
          top: `${Math.min(85, anchor.top + 3)}%`,
          left: `${anchor.left}%`,
          transform: 'translateX(-50%)',
          background: '#fff',
          border: '1px solid #ccc',
          borderRadius: 4,
          width: 240,
          maxHeight: 320,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 20,
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
        onMouseDown={(event) => event.stopPropagation()}
        // onMouseEnter={() => setHoveredAnnotationId(item.id)} //TODO - if it doesn't work, this change has to revert
        // onMouseLeave={() => setHoveredAnnotationId(null)} //TODO - if it doesn't work, this change has to revert
      >
        {/* Header with delete + close buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 8px',
            background: '#f0f0f0',
            borderBottom: '1px solid #e0e0e0',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => deleteAnnotation(item.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              color: '#cc0000',
              lineHeight: 1,
              padding: '0 2px',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
            title="Delete annotation"
          >
            🗑 Delete
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveAnnotationId(null)
              setHoveredAnnotationId(null)
              setThreadCommentText('')
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 16,
              color: '#666',
              lineHeight: 1,
              padding: '0 2px',
            }}
            title="Close"
          >
            ×
          </button>
        </div>
        {comments.length > 0 ? (
          <div style={{ maxHeight: 180, overflowY: 'auto', flexShrink: 0 }}>
            {comments.map((entry) => {
              const timeLabel = entry.created_dt
                ? formatDateTime(entry.created_dt, 'MMM D, YYYY - h:mm')
                : ''
              const authorName =
                entry?.created_by?.display_name || entry?.author || 'User'
              const initials = authorName
                .split(' ')
                .map((word) => word[0])
                .join('')
                .substring(0, 2)
                .toUpperCase()
              const avatarColors = [
                '#5c6bc0',
                '#26a69a',
                '#ef5350',
                '#ab47bc',
                '#ff7043',
              ]
              const avatarColor =
                avatarColors[authorName.charCodeAt(0) % avatarColors.length]
              return (
                <Box
                  key={entry.id}
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid #e5e5e5',
                    padding: '10px 12px',
                  }}
                >
                  <Box
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: avatarColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: '#fff',
                      }}
                    >
                      {initials}
                    </Typography>
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#1a1a1a',
                        }}
                      >
                        {authorName}
                      </Typography>
                      {timeLabel && (
                        <Typography
                          style={{
                            fontSize: '11px',
                            color: '#999',
                          }}
                        >
                          {timeLabel}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      style={{
                        fontSize: '12px',
                        color: '#444',
                        lineHeight: 1.4,
                        marginTop: 7,
                      }}
                    >
                      {entry.note}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </div>
        ) : (
          <div
            style={{
              padding: '10px 12px',
              fontSize: 13,
              color: '#666',
              borderBottom: '1px solid #e5e5e5',
            }}
          >
            No comments yet
          </div>
        )}

        <div style={{ padding: 10, background: '#f7f7f7' }}>
          <textarea
            placeholder="Write comments..."
            value={threadCommentText}
            onChange={(event) => setThreadCommentText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                addThreadComment()
              }
            }}
            style={{
              width: '100%',
              minHeight: 52,
              border: '1px solid #d0d0d0',
              borderRadius: 3,
              boxSizing: 'border-box',
              padding: 8,
              resize: 'vertical',
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 6,
              marginTop: 8,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setActiveAnnotationId(null)
                setHoveredAnnotationId(null)
                setThreadCommentText('')
              }}
              style={{
                background: '#f5f5f5',
                border: '1px solid #ccc',
                color: '#333',
                borderRadius: 3,
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addThreadComment}
              style={{
                background: '#26b7cc',
                border: 'none',
                color: '#fff',
                borderRadius: 3,
                padding: '4px 10px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Renderers ──────────────────────────────────────────────────────────────

  const renderShape = (item, index, isDraft = false) => {
    const key = isDraft ? 'draft' : item.id
    const geometry = getRenderedGeometry(item)
    const opacity = isDraft ? 0.6 : 1
    const isHighlighter = item.type === 'HIGHLIGHTER'
    const isActive =
      !isDraft &&
      (item.id === hoveredAnnotationId || item.id === activeAnnotationId)
    const color = isActive
      ? '#FFD700'
      : isHighlighter
        ? HIGHLIGHTER_COLOR
        : SHAPE_COLOR
    const moveCursor = mode === 'MOVE' ? 'grab' : 'default'
    const hoverCursor = mode !== 'MOVE' && !isDraft ? 'pointer' : moveCursor

    const label = !isDraft && (
      <div
        style={{
          position: 'absolute',
          top: -14,
          left: -2,
          background: color,
          color: isHighlighter ? '#333' : '#fff',
          fontSize: 10,
          padding: '0 4px',
          borderRadius: 2,
          whiteSpace: 'nowrap',
        }}
      >
        {index + 1}
      </div>
    )

    // POINT
    if (item.type === 'POINT') {
      return (
        <div
          key={key}
          onMouseDown={(e) => handleAnnotationMouseDown(e, item)}
          onMouseEnter={() => setHoveredAnnotationId(item.id)}
          onMouseLeave={() => setHoveredAnnotationId(null)}
          onClick={(event) => handleAnnotationClick(event, item.id)}
          style={{
            position: 'absolute',
            left: `${geometry.left || 0}%`,
            top: `${geometry.top || 0}%`,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: color,
            border: `2px solid ${isActive ? '#fff' : '#fff'}`,
            transform: 'translate(-50%, -50%)',
            cursor: hoverCursor,
            opacity,
            zIndex: 2,
          }}
        >
          {label}
        </div>
      )
    }

    // DRAWING / HIGHLIGHTER (freehand)
    if (item.type === 'DRAWING' || item.type === 'HIGHLIGHTER') {
      const pts = (geometry.points || []).map((p) => `${p.x},${p.y}`).join(' ')
      return (
        <svg
          key={key}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
            pointerEvents: 'none', // never intercept the whole canvas
            opacity,
          }}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* Invisible wide hit-area so thin strokes are easy to hover/click */}
          {!isDraft && (
            <polyline
              points={pts}
              fill="none"
              stroke="transparent"
              strokeWidth={8}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ pointerEvents: 'stroke', cursor: hoverCursor }}
              onMouseEnter={() => setHoveredAnnotationId(item.id)}
              onMouseLeave={() => setHoveredAnnotationId(null)}
              onClick={(event) => handleAnnotationClick(event, item.id)}
            />
          )}
          {/* Visible stroke */}
          <polyline
            points={pts}
            fill="none"
            stroke={color}
            strokeWidth={isHighlighter ? 3 : 1.5}
            strokeOpacity={isHighlighter ? 0.5 : 1}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: 'none' }}
          />
        </svg>
      )
    }

    // OVAL
    if (item.type === 'OVAL') {
      const showResizeHandles =
        !isDraft &&
        (isActive || hoveredAnnotationId === item.id) &&
        mode === 'MOVE'
      return (
        <div
          key={key}
          onMouseDown={(e) => handleAnnotationMouseDown(e, item)}
          onMouseEnter={() => setHoveredAnnotationId(item.id)}
          onMouseLeave={() => setHoveredAnnotationId(null)}
          onClick={(event) => handleAnnotationClick(event, item.id)}
          style={{
            position: 'absolute',
            left: `${geometry.left || 0}%`,
            top: `${geometry.top || 0}%`,
            width: `${geometry.width || 0}%`,
            height: `${geometry.height || 0}%`,
            border: isActive ? `2px dashed ${color}` : `2px solid ${color}`,
            borderRadius: '50%',
            boxSizing: 'border-box',
            cursor: hoverCursor,
            opacity,
          }}
        >
          {label}
          {showResizeHandles && renderResizeHandles(item)}
        </div>
      )
    }

    // RECTANGLE / HIGHLIGHTER (box)
    const showResizeHandles =
      !isDraft &&
      (isActive || hoveredAnnotationId === item.id) &&
      mode === 'MOVE'
    return (
      <div
        key={key}
        onMouseDown={(e) => handleAnnotationMouseDown(e, item)}
        onMouseEnter={() => setHoveredAnnotationId(item.id)}
        onMouseLeave={() => setHoveredAnnotationId(null)}
        onClick={(event) => handleAnnotationClick(event, item.id)}
        style={{
          position: 'absolute',
          left: `${geometry.left || 0}%`,
          top: `${geometry.top || 0}%`,
          width: `${geometry.width || 0}%`,
          height: `${geometry.height || 0}%`,
          border: isDraft
            ? `1px dashed ${color}`
            : isActive
              ? `2px dashed ${color}`
              : `2px solid ${color}`,
          background: isHighlighter ? `${color}33` : 'transparent',
          boxSizing: 'border-box',
          cursor: hoverCursor,
          opacity,
        }}
      >
        {label}
        {showResizeHandles && renderResizeHandles(item)}
      </div>
    )
  }

  // ── Guard ──────────────────────────────────────────────────────────────────

  if (!enableAnnotations) return null

  const cursorStyle =
    mode === 'MOVE' ? (dragState ? 'grabbing' : 'default') : 'crosshair'

  // Draft shape data normalised for renderShape
  const draftShape = draft
    ? {
        type: draft.type,
        geometry: {
          ...(draft.box || {}),
          points: draft.geometry?.points || [],
          left: draft.box?.left,
          top: draft.box?.top,
          width: draft.box?.width,
          height: draft.box?.height,
        },
      }
    : null

  // Clamp comment popup so it stays inside the image
  const popupLeft = pending
    ? Math.min(pending?.geometry?.anchorX ?? pending?.anchorX ?? 0, 65)
    : 0
  const popupTop = pending
    ? Math.min(pending?.geometry?.anchorY ?? pending?.anchorY ?? 0, 60)
    : 0

  return (
    <div
      style={{ position: 'absolute', inset: 0, cursor: cursorStyle }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          setActiveAnnotationId(null)
        }
        handleMouseDown(event)
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Rendered saved annotations */}
      {annotations.map((item, index) => renderShape(item, index))}

      {/* In-progress draft */}
      {draftShape && renderShape(draftShape, annotations.length, true)}

      {/* Active annotation threaded comments */}
      {renderThreadOverlay(
        annotations.find((item) => item.id === activeAnnotationId) || {}
      )}

      {/* Inline comment editor */}
      {pending && (
        <div
          style={{
            position: 'absolute',
            left: `${popupLeft}%`,
            top: `${popupTop}%`,
            zIndex: 10,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            minWidth: 200,
            maxHeight: 260,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <textarea
            autoFocus
            placeholder="Write description"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) submitAnnotation()
              if (e.key === 'Escape') cancelAnnotation()
            }}
            style={{
              width: '100%',
              minHeight: 64,
              fontFamily: 'monospace',
              fontSize: 13,
              resize: 'vertical',
              border: '1px solid #ddd',
              borderRadius: 2,
              padding: 4,
              boxSizing: 'border-box',
            }}
          />
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              gap: 6,
              justifyContent: 'flex-end',
            }}
          >
            <button
              onClick={cancelAnnotation}
              style={{
                padding: '3px 12px',
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: 2,
                background: '#f5f5f5',
              }}
            >
              Cancel
            </button>
            <button
              onClick={submitAnnotation}
              style={{
                padding: '3px 12px',
                background: '#cc0000',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 2,
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Hint label */}
      {!pending && (
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            background: 'rgba(50,50,50,0.7)',
            color: '#fff',
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 4,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {mode === 'MOVE'
            ? 'Drag to move • Drag handles to resize'
            : 'Click and Drag to Annotate'}
        </div>
      )}
    </div>
  )
}
